import React, { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Row, Col, Table, Card, CardBody, Alert, Spinner } from 'reactstrap'
import { API_URL } from './utils/Constants/URLS'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import { useSelector } from 'react-redux'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faDownload, faArrowLeft } from '@fortawesome/free-solid-svg-icons'
import PageHeader from './utils/PageHeader'

const FocSoDetails = () => {
    const token = localStorage.getItem('token')
    const [soData, setSoData] = useState({})
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState('')
    const [showDeclineModal, setShowDeclineModal] = useState(false)
    const [showSuccessModal, setShowSuccessModal] = useState(false)
    const [reasonForRejection, setReasonForRejection] = useState('')

    const [submitting, setSubmitting] = useState(false)
    const [modalMessage, setModalMessage] = useState('')
    const { soid } = useParams()
    const userDetails = useSelector(store => store.user)
    const navigate = useNavigate()

    // Fetch FOC Sales Order Details
    useEffect(() => {
        const fetchFocSO = async () => {
            setLoading(true)
            try {
                const res = await fetch(`${API_URL}foc/sales-order/${soid}`, {
                    method: 'GET',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: `Bearer ${token}`
                    }
                })
                const result = await res.json()
                if (result.error === false) {
                    setSoData(result?.data || {})
                } else {
                    setError(result.message || 'Failed to fetch FOC Sales Order')
                }
            } catch (e) {
                console.log('Error fetching FOC Sales Order:', e)
                setError('Failed to fetch FOC Sales Order details')
            } finally {
                setLoading(false)
            }
        }
        fetchFocSO()
    }, [token, soid])

    const downloadFile = async filePath => {
        try {
            const url = `${API_URL}file/serve`

            const response = await fetch(url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({ filePath })
            })

            if (!response.ok) return

            const blob = await response.blob()
            const downloadUrl = window.URL.createObjectURL(blob)

            const a = document.createElement('a')
            a.href = downloadUrl
            a.download = filePath.split('/').pop() || 'download'
            document.body.appendChild(a)
            a.click()
            a.remove()

            window.URL.revokeObjectURL(downloadUrl)
        } catch (err) {
            console.error('Error downloading file:', err)
        }
    }

    // Handle Approval - Commercial Manager Only
    const handleApprove = async () => {
        if (userDetails?.role !== 'commercial_manager') {
            alert('Only Commercial Manager can approve FOC Sales Orders')
            return
        }

        setSubmitting(true)
        try {
            const res = await fetch(`${API_URL}foc/sales-order/${soid}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    status: 'approved'
                })
            })
            const result = await res.json()
            if (result.error === false) {
                setModalMessage('FOC Sales Order approved successfully!')
                setShowSuccessModal(true)
            } else {
                alert(result.message || 'Error approving FOC Sales Order')
            }
        } catch (e) {
            console.log('Error approving FOC Sales Order:', e)
            alert('Error approving FOC Sales Order')
        } finally {
            setSubmitting(false)
        }
    }

    // Handle Decline Button Click
    const handleDeclineClick = () => {
        if (userDetails?.role !== 'commercial_manager') {
            alert('Only Commercial Manager can reject FOC Sales Orders')
            return
        }
        setShowDeclineModal(true)
    }

    // Handle Decline Modal Close
    const handleCloseDeclineModal = () => {
        setShowDeclineModal(false)
        setReasonForRejection('')
    }

    // Handle Decline Confirmation
    const handleDeclineConfirm = async () => {
        if (!reasonForRejection.trim()) {
            alert('Please enter a reason for rejection')
            return
        }

        setSubmitting(true)
        try {
            const res = await fetch(`${API_URL}foc/sales-order/${soid}`, {
                method: 'PUT',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: JSON.stringify({
                    status: 'rejected',
                    reasonForRejection: reasonForRejection
                })
            })
            const result = await res.json()
            if (result.error === false) {
                setModalMessage('FOC Sales Order rejected successfully')
                setShowDeclineModal(false)
                setReasonForRejection('')
                setShowSuccessModal(true)
            } else {
                alert(result.message || 'Error rejecting FOC Sales Order')
            }
        } catch (e) {
            console.log('Error rejecting FOC Sales Order:', e)
            alert('Error rejecting FOC Sales Order')
        } finally {
            setSubmitting(false)
        }
    }

    const handleSuccessModalClose = () => {
        setShowSuccessModal(false)
        navigate('/view_foc_so')
    }

    const formatDate = date =>
        date
            ? new Date(date).toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            })
            : '-'

    // Check if user is Commercial Manager
    const isCommercialManager = userDetails?.role === 'commercial_manager'

    // Check if SO is already approved or rejected
    const isApproved = soData?.status === 'approved'
    const isRejected = soData?.status === 'rejected'
    const isPending = !isApproved && !isRejected

    if (loading) {
        return (
            <>
                <PageHeader
                    pageName='FOC SALES ORDER DETAILS'
                    breadcrump1='Home'
                    breadcrump2='FOC SO Details'
                    link='/view_foc_so'
                />
                <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3 text-center'>
                    <Spinner color='primary' />
                    <p className='mt-2'>Loading FOC Sales Order Details...</p>
                </div>
            </>
        )
    }

    if (error) {
        return (
            <>
                <PageHeader
                    pageName='FOC SALES ORDER DETAILS'
                    breadcrump1='Home'
                    breadcrump2='FOC SO Details'
                    link='/view_foc_so'
                />
                <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3'>
                    <Alert color='danger'>{error}</Alert>
                    <Button color='secondary' onClick={() => navigate('/view_foc_so')}>
                        <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
                        Back to FOC Sales Orders
                    </Button>
                </div>
            </>
        )
    }

    return (
        <>
            <PageHeader
                pageName='FOC SALES ORDER DETAILS'
                breadcrump1='Home'
                breadcrump2='FOC SO Details'
                link='/view_foc_so'
            />
            <div className='main lg:p-6 xl:p-6 2xl:p-6 sm:p-3 md:p-3'>
                <Card className='p-4'>
                    <CardBody>
                        {/* Status Banner */}
                        {isApproved && (
                            <Alert color='success' className='mb-4'>
                                <i className='bi bi-check-circle-fill me-2'></i>
                                <strong>Approved</strong> - This FOC Sales Order has been approved.
                                {soData?.remarks && <p className='mb-0 mt-2'>Remarks: {soData.remarks}</p>}
                            </Alert>
                        )}
                        {isRejected && (
                            <Alert color='danger' className='mb-4'>
                                <i className='bi bi-x-circle-fill me-2'></i>
                                <strong>Rejected</strong> - This FOC Sales Order has been rejected.
                                {soData?.remarks && <p className='mb-0 mt-2'>Reason: {soData.remarks}</p>}
                            </Alert>
                        )}
                        {isPending && (
                            <Alert color='warning' className='mb-4'>
                                <i className='bi bi-clock-fill me-2'></i>
                                <strong>Pending Approval</strong> - Awaiting Commercial Manager approval.
                            </Alert>
                        )}

                        {/* Basic Details */}
                        <Row>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Sales Order Number</strong>
                                </label>
                                <p>{soData?.salesOrderNumber || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Lead ID</strong>
                                </label>
                                <p>{soData?.lead?.leadId || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Lead Journey Name</strong>
                                </label>
                                <p>{soData?.lead?.leadJourneyName || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Client</strong>
                                </label>
                                <p>{soData?.client?.companyName || soData?.lead?.client?.companyName || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Date of SO</strong>
                                </label>
                                <p>{formatDate(soData?.createdAt)}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Zero Date Delivery</strong>
                                </label>
                                <p>{formatDate(soData?.zeroDateOfDelivery)}</p>
                            </Col>
                            {soData?.isPOAvailable === true || soData?.isPOAvailable === 'true' ? (
                                <>
                                    <Col md={4}>
                                        <label className='form-label'>
                                            <strong>Customer PO Number</strong>
                                        </label>
                                        <p>
                                            {soData?.POFilePath ? (
                                                <button
                                                    type='button'
                                                    className='btn btn-link p-0 text-dark'
                                                    onClick={() => downloadFile(soData?.POFilePath)}
                                                >
                                                    {soData?.customerPONumber || '-'}
                                                </button>
                                            ) : (
                                                soData?.customerPONumber || '-'
                                            )}
                                        </p>
                                    </Col>
                                    <Col md={4}>
                                        <label className='form-label'>
                                            <strong>Customer PO Date</strong>
                                        </label>
                                        <p>{formatDate(soData?.customerPODate)}</p>
                                    </Col>
                                </>
                            ) : (
                                <Col md={8}>
                                    <label className='form-label'>
                                        <strong>PO Details</strong>
                                    </label>
                                    <p>{soData?.PODetails || '-'}</p>
                                </Col>
                            )}
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>GSPL GSTIN</strong>
                                </label>
                                <p>{soData?.gsplGSTNumber || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Consultant (If Any)</strong>
                                </label>
                                <p>{soData?.consultant || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Currency of Transaction</strong>
                                </label>
                                <p>{soData?.currencyOfTransaction || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Warranty Period (from commissioning)</strong>
                                </label>
                                <p>{soData?.warrantyPeriod ? `${soData.warrantyPeriod} Months` : '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Warranty Period (from last supply)</strong>
                                </label>
                                <p>{soData?.warrantyTerms ? `${soData.warrantyTerms} Months` : '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Billing Address</strong>
                                </label>
                                <p>{soData?.billingAddress || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Shipping Address</strong>
                                </label>
                                <p>{soData?.shippingAddress || '-'}</p>
                            </Col>
                            <Col md={4}>
                                <label className='form-label'>
                                    <strong>Remarks</strong>
                                </label>
                                <p>{soData?.remarks || '-'}</p>
                            </Col>
                            {soData?.BOMFilePath && (
                                <Col md={4}>
                                    <label className='form-label'>
                                        <strong>BOM</strong>
                                    </label>
                                    <p>
                                        <button
                                            type='button'
                                            className='btn download-btn mr-2'
                                            onClick={() => downloadFile(soData?.BOMFilePath)}
                                        >
                                            <FontAwesomeIcon icon={faDownload} />
                                        </button>
                                    </p>
                                </Col>
                            )}
                        </Row>

                        {/* Line Items Table */}
                        <div className='avoid-page-break mt-4'>
                            <h5 className='mb-3'>Line Items</h5>
                            <Table className='so-table' responsive>
                                <thead>
                                    <tr>
                                        <th>SI</th>
                                        <th>Name</th>
                                        <th>Part Code</th>
                                        <th>UOM</th>
                                        <th>Qty</th>
                                        <th>LP</th>
                                        <th>Discount</th>
                                        <th>Rate Per Unit</th>
                                        <th>Order Value</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {soData?.lineItems?.length > 0 ? (
                                        soData.lineItems.map((data, index) => (
                                            <tr key={index}>
                                                <td>{index + 1}</td>
                                                <td>{data?.model?.modelName || '-'}</td>
                                                <td>{data?.model?.partCode || '-'}</td>
                                                <td>{data?.model?.uom || '-'}</td>
                                                <td>{data?.quantity || 0}</td>
                                                <td>{(data?.listPrice || 0).toFixed(2)}</td>
                                                <td>{(data?.discount || 0).toFixed(2)}%</td>
                                                <td>{(data?.rate || 0).toFixed(2)}</td>
                                                <td>{(data?.value || 0).toFixed(2)}</td>
                                            </tr>
                                        ))
                                    ) : (
                                        <tr>
                                            <td colSpan='9' className='text-center'>
                                                No line items found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </Table>
                        </div>

                        {/* Payment Terms Table */}
                        <Row className='mt-3'>
                            <Col md={8}>
                                <div className='avoid-page-break'>
                                    <h5 className='mb-3'>Payment Terms</h5>
                                    <Table className='so-table'>
                                        <thead>
                                            <tr>
                                                <th>Payment Terms</th>
                                                <th>Percentage</th>
                                                <th>Amount</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {soData?.paymentTerms?.length > 0 ? (
                                                soData.paymentTerms.map((data, index) => (
                                                    <tr key={index}>
                                                        <td>{data?.type || '-'}</td>
                                                        <td>{data?.percentage || 0}%</td>
                                                        <td>{(data?.amount || 0).toFixed(2)}</td>
                                                    </tr>
                                                ))
                                            ) : (
                                                <tr>
                                                    <td colSpan='3' className='text-center'>
                                                        No payment terms found
                                                    </td>
                                                </tr>
                                            )}
                                        </tbody>
                                    </Table>

                                    {/* Extra Details Table */}
                                    <Table className='so-table mt-3'>
                                        <tbody>
                                            <tr>
                                                <td>
                                                    <strong>Price Basis:</strong>
                                                </td>
                                                <td>{soData?.priceBasis || '-'}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <strong>Responsibility of Transportation:</strong>
                                                </td>
                                                <td>{soData?.responsibilityOfTransportation || '-'}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <strong>Type of Transaction:</strong>
                                                </td>
                                                <td>{soData?.typeOfTransaction || '-'}</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <strong>GST Details:</strong>
                                                </td>
                                                <td>GST extra as applicable</td>
                                            </tr>
                                            <tr>
                                                <td>
                                                    <strong>Bank Guarantee Details:</strong>
                                                </td>
                                                <td>
                                                    {Array.isArray(soData?.bankGuaranteeDetails)
                                                        ? soData.bankGuaranteeDetails.join(', ')
                                                        : soData?.bankGuaranteeDetails || '-'}
                                                </td>
                                            </tr>
                                        </tbody>
                                    </Table>
                                </div>
                            </Col>
                        </Row>

                        {/* Approval Actions - Only for Commercial Manager and Pending Status */}
                        {isCommercialManager && isPending && (
                            <>


                                {/* Approve / Decline Buttons */}
                                <Row className='mt-4'>
                                    <Col className='d-flex justify-content-end gap-2'>
                                        <button
                                            type='button'
                                            onClick={handleApprove}
                                            className='btn btn-success'
                                            disabled={submitting}
                                        >
                                            {submitting ? 'Processing...' : 'Approve'}
                                        </button>
                                        <button
                                            type='button'
                                            onClick={handleDeclineClick}
                                            className='btn btn-danger'
                                            disabled={submitting}
                                        >
                                            Reject
                                        </button>
                                    </Col>
                                </Row>
                            </>
                        )}

                        {/* Back Button for non-Commercial Managers or already processed */}
                        {(!isCommercialManager || !isPending) && (
                            <Row className='mt-4'>
                                <Col className='d-flex justify-content-end'>
                                    <button
                                        type='button'
                                        onClick={() => navigate('/view_foc_so')}
                                        className='btn btn-secondary'
                                    >
                                        <FontAwesomeIcon icon={faArrowLeft} className='me-2' />
                                        Back to FOC Sales Orders
                                    </button>
                                </Col>
                            </Row>
                        )}
                    </CardBody>

                    {/* Success Modal */}
                    <Modal show={showSuccessModal} onHide={handleSuccessModalClose} centered>
                        <Modal.Body>
                            <div className='my-8 text-center'>
                                <svg
                                    xmlns='http://www.w3.org/2000/svg'
                                    className='w-14 shrink-0 fill-green-500 inline'
                                    viewBox='0 0 512 512'
                                >
                                    <path d='M383.841 171.838c-7.881-8.31-21.02-8.676-29.343-.775L221.987 296.732l-63.204-64.893c-8.005-8.213-21.13-8.393-29.35-.387-8.213 7.998-8.386 21.137-.388 29.35l77.492 79.561a20.687 20.687 0 0 0 14.869 6.275 20.744 20.744 0 0 0 14.288-5.694l147.373-139.762c8.316-7.888 8.668-21.027.774-29.344z' />
                                    <path d='M256 0C114.84 0 0 114.84 0 256s114.84 256 256 256 256-114.84 256-256S397.16 0 256 0zm0 470.487c-118.265 0-214.487-96.214-214.487-214.487 0-118.265 96.221-214.487 214.487-214.487 118.272 0 214.487 96.221 214.487 214.487 0 118.272-96.215 214.487-214.487 214.487z' />
                                </svg>
                                <h4 className='text-xl text-gray-800 font-semibold mt-4'>Success</h4>
                                <p className='text-sm text-gray-500 leading-relaxed mt-4'>{modalMessage}</p>
                            </div>

                            <button
                                type='button'
                                onClick={handleSuccessModalClose}
                                className='px-5 py-2.5 w-full rounded-lg text-white text-sm border-none outline-none bg-green-500 hover:bg-green-700'
                            >
                                Okay
                            </button>
                        </Modal.Body>
                    </Modal>

                    {/* Decline Modal */}
                    <Modal show={showDeclineModal} onHide={handleCloseDeclineModal} centered>
                        <Modal.Header className='bg-red-100' closeButton>
                            <Modal.Title>Reject FOC Sales Order</Modal.Title>
                        </Modal.Header>
                        <Modal.Body>
                            <form>
                                <div className='my-4'>
                                    <label className='form-label'>
                                        <strong>Reason for Rejection <span className='text-danger'>*</span></strong>
                                    </label>
                                    <textarea
                                        className='form-control'
                                        value={reasonForRejection}
                                        onChange={e => setReasonForRejection(e.target.value)}
                                        placeholder='Enter the reason for rejecting this FOC Sales Order'
                                        rows={4}
                                    />
                                </div>

                                <div className='d-flex gap-3'>
                                    <button
                                        onClick={handleCloseDeclineModal}
                                        type='button'
                                        className='btn btn-secondary flex-grow-1'
                                        disabled={submitting}
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type='button'
                                        onClick={handleDeclineConfirm}
                                        className='btn btn-danger flex-grow-1'
                                        disabled={submitting}
                                    >
                                        {submitting ? 'Processing...' : 'Reject'}
                                    </button>
                                </div>
                            </form>
                        </Modal.Body>
                    </Modal>
                </Card>
            </div>
        </>
    )
}

export default FocSoDetails
